import {
  Stack,
  Box,
  Tabs,
  Tab,
  Divider,
  Button,
  Grid,
  Typography,
  Link,
} from "@mui/material";
import viewpaths from "global/constants/ViewPathConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useLocation } from "react-router-dom";
import history from "utils/history";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import PaymentMethods from "./PaymentMethods/PaymentMethods";
import AccountUsage from "./AccountUsage/AccountUsage";
import UpgradePlan from "./UpgradePlan/UpgradePlan";
import BillingDetails from "./BillingDetails/BillingDetails";
import { handleErrorMessage } from "helpers/methods";
import { selectAccount } from "redux/authSlice";
import { useAppSelector } from "utils/hooks";
import {
  getAccountPlanDetail,
  featurePlan,
  checkActivePlan,
} from "./PlanService";
import { MyPlanContext } from "./Context";
import AddPaymentMethodDialog from "./Components/AddPaymentMethodDialog";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import urls from "global/constants/UrlConstants";
import Checkout from "./Checkout/Checkout";
import { PricingData } from "models/interfaces";
import BackIcon from "assets/icons1/back_arrow.svg";
import KeyboardBackspace from "@mui/icons-material/KeyboardBackspace";

const tabs = [
  {
    label: "Account Usage",
    value: "Account Usage",
    key: "account-usage",
  },
  {
    label: "Upgrade Plan",
    value: "Upgrade Plan",
    key: "upgrade-plan",
  },
  {
    label: "Payment Methods",
    value: "Payment Methods",
    key: "payment-methods",
  },
  {
    label: "Billing History",
    value: "Billing History",
    key: "billing-history",
  },
];

const MyPlan = () => {
  const getTab = new URLSearchParams(useLocation().search).get("tab");
  const promise = loadStripe(urls.STRIPE_PUBLIC_KEY);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [upgradePlan, setUpgradePlan] = useState<boolean>(false);
  const [checkounted, setCheckounted] = useState<boolean>(false);
  const [planCanceled, setPlanCanceled] = useState<boolean>(false);
  const [isActivePlan, setIsActivePlan] = useState<boolean>(false);
  const [checkOutBack, setCheckOutBack] = useState<boolean>(false);
  const [accountPlan, setAccountPlan] = useState<any>({});
  const [featurePlanDetails, setFeaturePlanDetails] = useState<any>({});
  const [selectedPlan, setSelectedPlan] = useState<PricingData>(
    {} as PricingData
  );
  const [openAddPaymentMethodDialog, setOpenAddPaymentMethodDialog] =
    useState<boolean>(false);
  const [doesNewPaymentMethodAdded, setDoesNewPaymentMethodAdded] =
    useState<boolean>(false);
  const accountName = useAppSelector(selectAccount);

  useEffect(() => {
    billingUsageApiHandler();
  }, [checkounted, planCanceled]);

  useEffect(() => {
    setUpgradePlan(false);
    setPlanCanceled(false);
    const tabToSelect = fetchTab(getTab ?? "", "key");
    setCurrentTab(tabToSelect?.label ?? tabs[0].value);
  }, [getTab]);

  const fetchTab = (
    selectedTab: string = "",
    searchBasedOn: "label" | "key"
  ) => {
    const newSelection = tabs.find((tab) => tab[searchBasedOn] === selectedTab);
    return newSelection;
  };

  const tabToSelect = fetchTab(getTab ?? "", "key");

  const [currentTab, setCurrentTab] = useState(
    tabToSelect?.label ?? tabs[0].label
  );

  const billingUsageApiHandler = async () => {
    try {
      setIsLoading(true);
      const [accountDetail, fetaureResponse, activePlan] = await Promise.all([
        getAccountPlanDetail(),
        featurePlan(accountName),
        checkActivePlan(),
      ]);
      setAccountPlan(accountDetail);
      setFeaturePlanDetails(fetaureResponse);
      setIsActivePlan(activePlan);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      const selectedTab = fetchTab(value, "label");
      history.replace(
        `${viewpaths.viewMyPlanViewPath}?tab=${selectedTab!.key}`
      );
      setCurrentTab(value);
    },
    []
  );

  const actionButtonBasedOnTab = (tab: string) => {
    switch (tab) {
      case "Account Usage": {
        return <></>;
      }
      case "Upgrade Plan": {
        return <></>;
      }
      case "Payment Methods": {
        return (
          <>
            {featurePlanDetails.price !== 0 && (
              <Button
                variant="contained"
                size="small"
                disabled={isLoading}
                onClick={() => setOpenAddPaymentMethodDialog(true)}
              >
                Add Payment Method
              </Button>
            )}
          </>
        );
      }
      case "Billing History": {
        return <></>;
      }
      default:
        break;
    }
  };

  const handleCheckOutBack = () => {
    upgradePlan && setCheckOutBack(true);
    setUpgradePlan(false);
    history.push(`${viewpaths.viewMyPlanViewPath}?tab=upgrade-plan`);
  };

  const getMyPlanPageHeader = () => {
    return (
      <>
        <Stack
          sx={{
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" alignItems="center">
            {upgradePlan ? (
              <Link
                color="text.primary"
                sx={{
                  alignItems: "center",
                  display: "inline-flex",
                  cursor: upgradePlan ? "pointer" : "normal",
                }}
                onClick={() => handleCheckOutBack()}
                underline="hover"
                component={"button"}
              >
                {upgradePlan && (
                  <KeyboardBackspace
                    htmlColor="#475569"
                    sx={{ cursor: "pointer", pr: 1 }}
                  />
                )}
                <Typography
                  variant="body1"
                  color="text.primary"
                  fontWeight={700}
                >
                  Checkout
                </Typography>
              </Link>
            ) : (
              <Typography variant="h5" color="text.primary" fontWeight={700}>
                My Plan
              </Typography>
            )}
          </Stack>
          <Stack alignItems="center" direction="row" spacing={2}>
            {actionButtonBasedOnTab(currentTab)}
          </Stack>
        </Stack>
        {!upgradePlan && (
          <Stack direction="column">
            <Box>
              <Tabs
                indicatorColor="primary"
                onChange={handleTabsChange}
                scrollButtons="auto"
                textColor="primary"
                value={currentTab}
                variant="scrollable"
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                    disabled={isLoading}
                  />
                ))}
              </Tabs>
              <Divider />
            </Box>
          </Stack>
        )}
      </>
    );
  };

  const getAddPaymentMethodDialog = () => {
    return (
      <Elements stripe={promise}>
        <AddPaymentMethodDialog
          isDialogOpen={openAddPaymentMethodDialog}
          setDialogOpen={setOpenAddPaymentMethodDialog}
          doesNewPaymentMethodAdded={doesNewPaymentMethodAdded}
          setDoesNewPaymentMethodAdded={setDoesNewPaymentMethodAdded}
        />
      </Elements>
    );
  };

  const getMyPlanPageBody = () => {
    if (upgradePlan) {
      return (
        <Elements stripe={promise}>
          <Checkout checkounted={checkounted} setCheckounted={setCheckounted} />
        </Elements>
      );
    }
    switch (currentTab) {
      case "Account Usage":
        return (
          <AccountUsage
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            planCanceled={planCanceled}
            setPlanCanceled={setPlanCanceled}
          />
        );
      case "Upgrade Plan":
        return (
          <UpgradePlan
            upgradePlan={upgradePlan}
            setUpgradePlan={setUpgradePlan}
          />
        );
      case "Payment Methods":
        return (
          <PaymentMethods
            doesNewPaymentMethodAdded={doesNewPaymentMethodAdded}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
          />
        );
      case "Billing History":
        return <BillingDetails />;
      default:
        break;
    }
  };

  const getMyPlanPage = () => {
    return (
      <Stack direction="column" spacing={1}>
        {getMyPlanPageHeader()}
        <Box pt={2}>{getMyPlanPageBody()}</Box>
        {getAddPaymentMethodDialog()}
      </Stack>
    );
  };

  return (
    <MyPlanContext.Provider
      value={{
        accountPlan,
        setAccountPlan,
        featurePlanDetails,
        setFeaturePlanDetails,
        selectedPlan,
        setSelectedPlan,
        isActivePlan,
        setIsActivePlan,
      }}
    >
      <CenteredAuthenticatedLayout>
        {getMyPlanPage()}
      </CenteredAuthenticatedLayout>
    </MyPlanContext.Provider>
  );
};

export default MyPlan;
